import { UnknownAction } from 'redux';
import { store, RootState } from '@src/store';
import { SAVE } from '@src/store/consents';
import { ConsentType } from '@src/types/common';
import { getAllFeatures, haveUncheckedFeatures } from '@src/util/consents';
import { service } from './index';

export type Tracker = {
  reduxActionListener: (arg0: UnknownAction) => void;
  doTracking: (name: string, details: Record<string, unknown>) => void;
};

type EventData = Record<string, unknown>;
interface MapperStateToEventData {
  (arg0: RootState): EventData;
}

export const TRACKER_EVENT_NAME_START_CHANGE_PERMISSION = 'startChangePermissions';
export const TRACKER_EVENT_NAME_CANCEL_CHANGE_PERMISSION = 'cancelChangePermissions';
export const TRACKER_EVENT_NAME_CHANGE_PERMISSION = 'changePermissions';
export const TRACKER_EVENT_NAME_CLICK_COLLAPSABLE = 'clickCollapsable';

const mapStateToEventDataStandard: MapperStateToEventData = ({
  config: { lifecycle: origin },
}) => ({
  origin,
});

type MapStateToEventData = Record<string, MapperStateToEventData>;

const mapStateToEventData: MapStateToEventData = {
  [TRACKER_EVENT_NAME_START_CHANGE_PERMISSION]: mapStateToEventDataStandard,
  [TRACKER_EVENT_NAME_CANCEL_CHANGE_PERMISSION]: mapStateToEventDataStandard,
  [TRACKER_EVENT_NAME_CLICK_COLLAPSABLE]: mapStateToEventDataStandard,
  [TRACKER_EVENT_NAME_CHANGE_PERMISSION]: ({
    config: { lifecycle: origin },
    consents: { consentGroups },
  }) => {
    const data: Record<string, unknown> = {
      origin,
    };
    consentGroups!.forEach(
      ({
        required, consents = [], oneclick, denyall,
      }, ind) => {
        consents.forEach(({ name, granted = false, ...features }) => {
          const allFeaturesAsArray = getAllFeatures(features as ConsentType);
          const hasFeatures = allFeaturesAsArray.length > 0;
          const featuresData = allFeaturesAsArray.reduce(
            (acc, { id, granted: fgranted = false }) => ({
              ...acc,
              [id]: fgranted,
            }),
            {},
          );

          if (hasFeatures) {
            data[name] = featuresData;
          } else {
            data[name] = required ? true : granted;
          }
        });

        const groupName = consentGroups![ind].name || ind;
        // set oc-<group> properties
        if (oneclick) {
          // calculate oneclick: true only if all granted
          const consentsHasUncheckedFeatures = haveUncheckedFeatures(consents);

          data[`oc-${groupName}`] = !consentsHasUncheckedFeatures;
        }

        // set ocw-<group> properties
        if (denyall) {
          const noConsentsSet = !consents.find((co) => co.granted);
          data[`ocw-${groupName}`] = noConsentsSet;
        }
      },
    );

    return data;
  },
} as const;

const doTracking = (name: string, data: Record<string, unknown> = {}) => {
  if (!(name in mapStateToEventData)) {
    return;
  }

  service.fireInfo('TRACKING', {
    data: {
      event: name,
      ...mapStateToEventData[name](store.getState()),
      ...data,
    },
  });
};

export const tracker: Tracker = {
  reduxActionListener: ({ type }) => {
    const events = {
      [SAVE]: TRACKER_EVENT_NAME_CHANGE_PERMISSION,
    } as const;
    if (!(type in events)) return; // action not to be tracked
    doTracking(events[type as keyof typeof events]);
  },
  doTracking,
};
